import { styled, useTranslation } from '@obvio/app'
import { SvgCloseMenu } from '@obvio/svg'
import {
  Stack,
  Grid,
  Form,
  TextInput,
  SelectInput,
  TextArea,
  Button,
  Submit,
  ImageInput,
  NumberInput,
} from '@obvio/ui'

import { useArtworksContext } from './FormArtworksContext'

import type { Artwork } from './FormArtwork'
import type { AllowUndefinedForOptional } from '@obvio/utils'
import type { ReactElement } from 'react'

const Wrap = styled(Stack)`
  border: 1px solid ${(theme) => theme.stroke};
  padding: ${(theme) => theme.spacing.large};
  margin-top: ${(theme) => theme.spacing.large};
`

const Selectors = styled(Grid)`
  width: 100%;
	grid-row
`

type FormArtworkInnerProps = AllowUndefinedForOptional<{
  id?: string
  save?(): void
  close(): void
}>

const FEATURES = {
  category: ['Painting', 'Sculpture', 'Photography', 'Drawing', 'Printmaking'],
  size: ['Small', 'Medium', 'Large'],
  rarity: ['Common', 'Rare', 'Unique'],
}

export function FormArtworkInner({
  id,
  save,
  close,
}: FormArtworkInnerProps): ReactElement {
  const { t } = useTranslation()
  const { addArtwork, data, editArtwork } = useArtworksContext()

  const handleSubmit = (artwork: Artwork) => {
    if (id) {
      editArtwork(id, artwork)
      save?.()
    } else {
      addArtwork(artwork)
    }
    close()
  }

  if (id && !data.has(id)) {
    throw new Error(`Artwork with id ${id} not found`)
  }

  const defaultValue = id ? data.get(id) : null

  return (
    <Form onSubmit={handleSubmit} contextOnly>
      <Wrap kind="vertical" fullWidth>
        {/** @ts-expect-error */}
        <ImageInput name="images" defaultValue={defaultValue?.images ?? []} />
        <TextInput
          name="title"
          label="Title"
          removeOnUnmount
          defaultValue={defaultValue?.title}
        />
        <NumberInput
          name="year"
          label="Year"
          removeOnUnmount
          defaultValue={defaultValue?.year}
        />
        <Selectors templateColumns="1fr 1fr" gap="medium">
          <SelectInput
            name="category"
            placeholder={t('joinForm.label.category')}
            defaultValue={defaultValue?.category}
            dropdownProps={{
              adherent: true,
            }}
          >
            {FEATURES.category.map((category) => (
              <SelectInput.Option key={category} value={category.toLowerCase()}>
                {category}
              </SelectInput.Option>
            ))}
          </SelectInput>
          <SelectInput
            name="size"
            placeholder={t('joinForm.label.size')}
            defaultValue={defaultValue?.size}
            dropdownProps={{
              adherent: true,
            }}
          >
            {FEATURES.size.map((size) => (
              <SelectInput.Option key={size} value={size.toLowerCase()}>
                {size}
              </SelectInput.Option>
            ))}
          </SelectInput>
          <SelectInput
            name="rarity"
            placeholder="CHOOSE RARITY"
            defaultValue={defaultValue?.rarity}
            dropdownProps={{
              adherent: true,
            }}
          >
            {FEATURES.rarity.map((rarity) => (
              <SelectInput.Option key={rarity} value={rarity.toLowerCase()}>
                {rarity}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Selectors>
        <TextArea
          name="description"
          label="Artwork description"
          rows={5}
          placeholder="eg 170 cm x 120cm  Photo taken on August 1-2, 2017"
          defaultValue={defaultValue?.description}
        />
        <Stack>
          <Submit>Save</Submit>
          <Button kind={['icon', 'ghost']} onClick={close}>
            <SvgCloseMenu />
          </Button>
        </Stack>
      </Wrap>
    </Form>
  )
}
